import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiBriefcase, FiDollarSign, FiFileText, FiPlus, FiX, FiEdit2, FiCheck, FiTrash2 } from 'react-icons/fi';
import { CreditAdjustModal } from './CreditAdjustModal';
import { apiGet, apiPut, apiPost, apiDelete } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { toast } from 'sonner';

interface Company {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  website?: string;
  logo?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminInvoice {
  index: number;
  cycleNumber: number;
  period: { start: string; end: string };
  planId?: string;
  planName?: string;
  payment?: { amount: number; discount: number; total: number; currency: string };
  summary: {
    balanceBefore: number;
    totalCreditsIn: number;
    totalCreditsOut: number;
    expired: number;
    balanceAfter: number;
  };
  status: string;
  closedAt?: string;
  createdAt: string;
}

interface InvoicesResponse {
  invoices: AdminInvoice[];
  subscriptionStatus: string | null;
}

interface CompanyEditDrawerProps {
  companyId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
];

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (amount: number, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);

const invoiceStatusBadge = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'closed') return 'success';
  if (status === 'cancelled') return 'error';
  return 'warning';
};

const invoiceStatusLabel = (status: string) => {
  if (status === 'closed') return 'Fechada';
  if (status === 'cancelled') return 'Cancelada';
  return 'Aberta';
};

const CURRENCY_OPTIONS = [
  { value: 'BRL', label: 'BRL (R$)' },
  { value: 'USD', label: 'USD (US$)' },
];

const INVOICE_STATUS_OPTIONS = [
  { value: 'closed', label: 'Fechada' },
  { value: 'open', label: 'Aberta' },
  { value: 'cancelled', label: 'Cancelada' },
];

interface AdminPlan {
  id: string;
  name: string;
  creditsPerCycle: number;
  price?: { monthly?: number; yearly?: number; currency?: string };
  status: string;
}

const emptyInvoiceForm = { planId: '', periodStart: '', periodEnd: '', amount: '', discount: '', currency: 'BRL', status: 'closed' };

export const CompanyEditDrawer: React.FC<CompanyEditDrawerProps> = ({ companyId, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', email: '', phone: '', website: '', status: 'active' });
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
  const [editingInvoiceIndex, setEditingInvoiceIndex] = useState<number | null>(null);
  const [editInvoiceForm, setEditInvoiceForm] = useState(emptyInvoiceForm);

  const { data: company, isLoading } = useQuery({
    queryKey: ['admin-company', companyId],
    queryFn: () => apiGet<Company>(`/companies/${companyId}`),
    enabled: !!companyId && isOpen,
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['admin-company-invoices', companyId],
    queryFn: () => apiGet<InvoicesResponse>(`/companies/${companyId}/invoices`),
    enabled: !!companyId && isOpen,
  });

  const { data: creditsData, isLoading: isLoadingCredits } = useQuery({
    queryKey: ['admin-company-credits', companyId],
    queryFn: async () => {
      const response = await apiGet<{
        balance: number;
        recentAdminTransactions: Array<{
          id: string;
          type: 'credit' | 'debit';
          amount: number;
          balanceAfter: number;
          description: string;
          adminName: string;
          createdAt: string;
        }>;
      }>(`/companies/${companyId}/credits`);
      return response;
    },
    enabled: !!companyId && isOpen,
  });

  const { data: plansData } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => apiGet<AdminPlan[]>('/plans'),
    enabled: isOpen,
  });

  const planOptions = (plansData || [])
    .filter((p) => p.status === 'active')
    .map((p) => ({ value: p.id, label: `${p.name} (${p.creditsPerCycle} créditos)` }));

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        website: company.website || '',
        status: company.status || 'active',
      });
    }
  }, [company]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, string>) => apiPut(`/companies/${companyId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      queryClient.invalidateQueries({ queryKey: ['admin-company', companyId] });
      toast.success('Company atualizada');
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Nome e obrigatorio');
      return;
    }
    updateMutation.mutate(form);
  };

  const createInvoiceMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiPost<{ cycleNumber: number; subscriptionActivated: boolean }>(`/companies/${companyId}/invoices`, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-company-invoices', companyId] });
      queryClient.invalidateQueries({ queryKey: ['admin-company-credits', companyId] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      setShowNewInvoice(false);
      setInvoiceForm(emptyInvoiceForm);
      if (result?.subscriptionActivated) {
        toast.success('Fatura criada e assinatura ativada com créditos injetados');
      } else {
        toast.success('Fatura criada');
      }
    },
  });

  const confirmPaymentMutation = useMutation({
    mutationFn: () => apiPost<{ message: string; cycleNumber: number; cycleEnd: string }>(`/companies/${companyId}/credits/confirm-payment`, {}),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-company-invoices', companyId] });
      queryClient.invalidateQueries({ queryKey: ['admin-company-credits', companyId] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      toast.success(result?.message || 'Pagamento confirmado');
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error?.response?.data?.message || 'Erro ao confirmar pagamento');
    },
  });

  const handleConfirmPayment = () => {
    if (confirmPaymentMutation.isPending) return;
    if (!window.confirm('Confirmar pagamento manual? Isso irá iniciar um novo ciclo de cobrança.')) return;
    confirmPaymentMutation.mutate();
  };

  const handleCreateInvoice = () => {
    if (!invoiceForm.periodStart || !invoiceForm.periodEnd) {
      toast.error('Periodo e obrigatorio');
      return;
    }
    if (!invoiceForm.amount || parseFloat(invoiceForm.amount) <= 0) {
      toast.error('Valor e obrigatorio');
      return;
    }
    createInvoiceMutation.mutate({
      planId: invoiceForm.planId || undefined,
      periodStart: invoiceForm.periodStart,
      periodEnd: invoiceForm.periodEnd,
      amount: parseFloat(invoiceForm.amount),
      discount: parseFloat(invoiceForm.discount) || 0,
      currency: invoiceForm.currency,
      status: invoiceForm.status,
    });
  };

  const updateInvoiceMutation = useMutation({
    mutationFn: ({ index, data }: { index: number; data: Record<string, unknown> }) =>
      apiPut(`/companies/${companyId}/invoices/${index}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-company-invoices', companyId] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      setEditingInvoiceIndex(null);
      toast.success('Fatura atualizada');
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: (index: number) =>
      apiDelete(`/companies/${companyId}/invoices/${index}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-company-invoices', companyId] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      toast.success('Fatura excluida');
    },
  });

  const handleEditInvoice = (inv: AdminInvoice) => {
    setEditingInvoiceIndex(inv.index);
    setEditInvoiceForm({
      planId: inv.planId || '',
      periodStart: inv.period.start ? new Date(inv.period.start).toISOString().split('T')[0] : '',
      periodEnd: inv.period.end ? new Date(inv.period.end).toISOString().split('T')[0] : '',
      amount: inv.payment?.amount?.toString() || '',
      discount: inv.payment?.discount?.toString() || '0',
      currency: inv.payment?.currency || 'BRL',
      status: inv.status,
    });
  };

  const handleSaveInvoice = () => {
    if (editingInvoiceIndex === null) return;
    const selectedPlan = plansData?.find(p => p.id === editInvoiceForm.planId);
    updateInvoiceMutation.mutate({
      index: editingInvoiceIndex,
      data: {
        planId: editInvoiceForm.planId || undefined,
        planName: selectedPlan?.name || undefined,
        periodStart: editInvoiceForm.periodStart,
        periodEnd: editInvoiceForm.periodEnd,
        amount: parseFloat(editInvoiceForm.amount) || 0,
        discount: parseFloat(editInvoiceForm.discount) || 0,
        currency: editInvoiceForm.currency,
        status: editInvoiceForm.status,
      },
    });
  };

  const handleDeleteInvoice = (inv: AdminInvoice) => {
    if (!confirm(`Excluir fatura #${inv.cycleNumber}?`)) return;
    deleteInvoiceMutation.mutate(inv.index);
  };

  const isSaving = updateMutation.isPending;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Editar Company"
      subtitle={company?.name}
      icon={<FiBriefcase className="w-5 h-5" />}
      disabled={isSaving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancelar</Button>
          <Button onClick={handleSubmit} loading={isSaving}>Salvar</Button>
        </>
      }
    >
      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={40} />)}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Company Info */}
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>Informacoes</h3>
            <div className="space-y-3">
              <FormField label="Nome" required>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Nome da company" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email">
                  <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" />
                </FormField>
                <FormField label="Telefone">
                  <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(00) 00000-0000" />
                </FormField>
              </div>
              <FormField label="Website">
                <Input value={form.website} onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
              </FormField>
              <FormField label="Status">
                <Select value={form.status} onChange={(v) => setForm(f => ({ ...f, status: v }))} options={STATUS_OPTIONS} />
              </FormField>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* Invoices (billing history) */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: '#1e293b' }}>
                <FiFileText className="w-4 h-4" /> Historico de Faturas
              </h3>
              <div className="flex items-center gap-2">
                {invoicesData?.subscriptionStatus && (
                  <Badge
                    variant={invoicesData.subscriptionStatus === 'active' ? 'success' : invoicesData.subscriptionStatus === 'past_due' ? 'warning' : 'error'}
                    size="sm"
                  >
                    {invoicesData.subscriptionStatus === 'active' ? 'Ativa' : invoicesData.subscriptionStatus === 'past_due' ? 'Inadimplente' : invoicesData.subscriptionStatus === 'cancelled' ? 'Cancelada' : invoicesData.subscriptionStatus}
                  </Badge>
                )}
                {invoicesData?.subscriptionStatus &&
                  ['active', 'past_due', 'suspended', 'awaiting_payment'].includes(invoicesData.subscriptionStatus) && (
                  <button
                    onClick={handleConfirmPayment}
                    disabled={confirmPaymentMutation.isPending}
                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg transition-colors"
                    style={{ color: '#16a34a', opacity: confirmPaymentMutation.isPending ? 0.5 : 1 }}
                    onMouseEnter={(e) => { if (!confirmPaymentMutation.isPending) e.currentTarget.style.backgroundColor = '#f0fdf4'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <FiCheck className="w-3.5 h-3.5" /> {confirmPaymentMutation.isPending ? 'Processando...' : 'Confirmar Pagamento'}
                  </button>
                )}
                {!showNewInvoice && (
                  <button
                    onClick={() => setShowNewInvoice(true)}
                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg transition-colors"
                    style={{ color: '#8b5cf6' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3e8ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <FiPlus className="w-3.5 h-3.5" /> Criar fatura
                  </button>
                )}
              </div>
            </div>

            {/* New invoice form */}
            {showNewInvoice && (
              <div className="rounded-lg border p-3 mb-3" style={{ borderColor: '#8b5cf6', backgroundColor: '#faf5ff' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium" style={{ color: '#1e293b' }}>Nova fatura</span>
                  <button onClick={() => { setShowNewInvoice(false); setInvoiceForm(emptyInvoiceForm); }} className="p-1 rounded" style={{ color: '#6b7280' }}>
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  <FormField label="Plano">
                    <Select
                      value={invoiceForm.planId}
                      onChange={(v) => setInvoiceForm(f => ({ ...f, planId: v }))}
                      options={[{ value: '', label: 'Sem plano' }, ...planOptions]}
                    />
                  </FormField>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Inicio do periodo" required>
                      <Input
                        type="date"
                        value={invoiceForm.periodStart}
                        onChange={(e) => setInvoiceForm(f => ({ ...f, periodStart: e.target.value }))}
                      />
                    </FormField>
                    <FormField label="Fim do periodo" required>
                      <Input
                        type="date"
                        value={invoiceForm.periodEnd}
                        onChange={(e) => setInvoiceForm(f => ({ ...f, periodEnd: e.target.value }))}
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <FormField label="Valor" required>
                      <Input
                        type="number"
                        step="0.01"
                        value={invoiceForm.amount}
                        onChange={(e) => setInvoiceForm(f => ({ ...f, amount: e.target.value }))}
                        placeholder="0.00"
                      />
                    </FormField>
                    <FormField label="Desconto">
                      <Input
                        type="number"
                        step="0.01"
                        value={invoiceForm.discount}
                        onChange={(e) => setInvoiceForm(f => ({ ...f, discount: e.target.value }))}
                        placeholder="0.00"
                      />
                    </FormField>
                    <FormField label="Moeda">
                      <Select
                        value={invoiceForm.currency}
                        onChange={(v) => setInvoiceForm(f => ({ ...f, currency: v }))}
                        options={CURRENCY_OPTIONS}
                      />
                    </FormField>
                  </div>
                  <FormField label="Status">
                    <Select
                      value={invoiceForm.status}
                      onChange={(v) => setInvoiceForm(f => ({ ...f, status: v }))}
                      options={INVOICE_STATUS_OPTIONS}
                    />
                  </FormField>
                  <Button onClick={handleCreateInvoice} loading={createInvoiceMutation.isPending} size="sm">
                    Criar fatura
                  </Button>
                </div>
              </div>
            )}

            {invoicesLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={48} />)}
              </div>
            ) : !invoicesData?.invoices || invoicesData.invoices.length === 0 ? (
              <p className="text-sm" style={{ color: '#6b7280' }}>Nenhuma fatura encontrada.</p>
            ) : (
              <div className="space-y-2">
                {invoicesData.invoices.map((inv) => (
                  <div
                    key={inv.cycleNumber}
                    className="rounded-lg border p-3"
                    style={{ borderColor: editingInvoiceIndex === inv.index ? '#8b5cf6' : '#e5e7eb', backgroundColor: editingInvoiceIndex === inv.index ? '#faf5ff' : '#f9fafb' }}
                  >
                    {editingInvoiceIndex === inv.index ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                            Fatura #{inv.cycleNumber}
                          </span>
                          <div className="flex gap-1">
                            <button onClick={handleSaveInvoice} className="p-1 rounded transition-colors" style={{ color: '#10b981' }} disabled={updateInvoiceMutation.isPending}>
                              <FiCheck className="w-4 h-4" />
                            </button>
                            <button onClick={() => setEditingInvoiceIndex(null)} className="p-1 rounded transition-colors" style={{ color: '#6b7280' }}>
                              <FiX className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <FormField label="Plano">
                          <Select
                            value={editInvoiceForm.planId}
                            onChange={(v) => setEditInvoiceForm(f => ({ ...f, planId: v }))}
                            options={[{ value: '', label: 'Sem plano' }, ...planOptions]}
                          />
                        </FormField>
                        <div className="grid grid-cols-2 gap-3">
                          <FormField label="Inicio">
                            <Input type="date" value={editInvoiceForm.periodStart} onChange={(e) => setEditInvoiceForm(f => ({ ...f, periodStart: e.target.value }))} />
                          </FormField>
                          <FormField label="Fim">
                            <Input type="date" value={editInvoiceForm.periodEnd} onChange={(e) => setEditInvoiceForm(f => ({ ...f, periodEnd: e.target.value }))} />
                          </FormField>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <FormField label="Valor">
                            <Input type="number" step="0.01" value={editInvoiceForm.amount} onChange={(e) => setEditInvoiceForm(f => ({ ...f, amount: e.target.value }))} />
                          </FormField>
                          <FormField label="Desconto">
                            <Input type="number" step="0.01" value={editInvoiceForm.discount} onChange={(e) => setEditInvoiceForm(f => ({ ...f, discount: e.target.value }))} />
                          </FormField>
                          <FormField label="Moeda">
                            <Select value={editInvoiceForm.currency} onChange={(v) => setEditInvoiceForm(f => ({ ...f, currency: v }))} options={CURRENCY_OPTIONS} />
                          </FormField>
                        </div>
                        <FormField label="Status">
                          <Select value={editInvoiceForm.status} onChange={(v) => setEditInvoiceForm(f => ({ ...f, status: v }))} options={INVOICE_STATUS_OPTIONS} />
                        </FormField>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="space-y-1 flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                              Fatura #{inv.cycleNumber}
                            </span>
                            <Badge variant={invoiceStatusBadge(inv.status)} size="sm">
                              {invoiceStatusLabel(inv.status)}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs" style={{ color: '#6b7280' }}>
                            <span>{formatDate(inv.period.start)} - {formatDate(inv.period.end)}</span>
                            {inv.planName && <span>{inv.planName}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          <div className="text-right">
                            {inv.payment ? (
                              <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                                {formatCurrency(inv.payment.total, inv.payment.currency)}
                              </span>
                            ) : (
                              <span className="text-xs" style={{ color: '#94a3b8' }}>-</span>
                            )}
                            <div className="text-[11px] mt-0.5" style={{ color: '#94a3b8' }}>
                              <span style={{ color: '#10b981' }}>+{inv.summary.totalCreditsIn}</span>
                              {' / '}
                              <span style={{ color: '#ef4444' }}>-{inv.summary.totalCreditsOut}</span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleEditInvoice(inv)}
                              className="p-1 rounded-lg transition-colors"
                              style={{ color: '#6b7280' }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                              title="Editar"
                            >
                              <FiEdit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteInvoice(inv)}
                              className="p-1 rounded-lg transition-colors"
                              style={{ color: '#ef4444' }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                              title="Excluir"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* Credits */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: '#1e293b' }}>
                <FiDollarSign className="w-4 h-4" /> Créditos
              </h3>
              <button
                onClick={() => setShowAdjustModal(true)}
                className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg transition-colors"
                style={{ color: '#8b5cf6' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3e8ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                Ajustar créditos
              </button>
            </div>

            {isLoadingCredits ? (
              <Skeleton height={60} />
            ) : (
              <>
                {/* Balance */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs" style={{ color: '#64748b' }}>Saldo atual:</span>
                  <span
                    className="text-sm font-bold px-2 py-0.5 rounded"
                    style={{
                      backgroundColor: (creditsData?.balance ?? 0) > 0 ? '#dcfce7' : '#fef2f2',
                      color: (creditsData?.balance ?? 0) > 0 ? '#166534' : '#991b1b',
                    }}
                  >
                    {(creditsData?.balance ?? 0).toLocaleString('pt-BR')} créditos
                  </span>
                </div>

                {/* Recent admin transactions */}
                {creditsData?.recentAdminTransactions && creditsData.recentAdminTransactions.length > 0 ? (
                  <div className="space-y-2">
                    <span className="text-xs font-medium" style={{ color: '#64748b' }}>Últimos ajustes:</span>
                    {creditsData.recentAdminTransactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between text-xs p-2 rounded-lg"
                        style={{ backgroundColor: '#f8fafc', border: '1px solid #f1f5f9' }}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                            style={{
                              backgroundColor: tx.type === 'credit' ? '#dcfce7' : '#fef2f2',
                              color: tx.type === 'credit' ? '#166534' : '#991b1b',
                            }}
                          >
                            {tx.type === 'credit' ? '+' : '-'}{tx.amount.toLocaleString('pt-BR')}
                          </span>
                          <span style={{ color: '#475569' }} className="truncate max-w-[200px]" title={tx.description}>
                            {tx.description}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span style={{ color: '#94a3b8' }}>{tx.adminName}</span>
                          <span style={{ color: '#cbd5e1' }}>
                            {new Date(tx.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: '#94a3b8' }}>Nenhum ajuste manual realizado</p>
                )}
              </>
            )}
          </div>

          <CreditAdjustModal
            isOpen={showAdjustModal}
            onClose={() => setShowAdjustModal(false)}
            companyId={companyId || ''}
          />

          {/* Meta info */}
          {company && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb' }} />
              <div className="flex items-center gap-4 text-xs" style={{ color: '#94a3b8' }}>
                <span>Criado em: {formatDate(company.createdAt)}</span>
                <span>Atualizado em: {formatDate(company.updatedAt)}</span>
              </div>
            </>
          )}
        </div>
      )}
    </Drawer>
  );
};
